import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Api } from 'Utils/api';

// Error types for specific handling
export type GHLErrorType = 'LOCATION_NOT_LINKED' | 'LOCATION_SELECTION_REQUIRED' | 'INVALID_TOKEN' | 'TIMEOUT' | 'GENERIC' | null;

// Available location returned by backend when selection is required
export interface GHLAvailableLocation {
  id: string;
  name: string;
}

// GHL User data received from parent iframe
export interface GHLUserData {
  ssoToken: string;
  locationId: string;
  locationName: string;
  role: string;
  userName: string;
  userId: string;
  companyId: string;
  userType: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  error: string | null;
  errorType: GHLErrorType;
  availableLocations: GHLAvailableLocation[];
  selectLocation: (locationId: string) => void;
}

// Response from /api/ghl/embed/me endpoint
interface GHLMeResponse {
  location_id: string;
  location_name?: string;
  company_id: number | string;
  available_locations?: GHLAvailableLocation[];
  payload: {
    activeLocation?: string;
    locationId?: string;
    location_id?: string;
    locationName?: string;
    role?: string;
    name?: string;
    userName?: string;
    userId?: string;
    companyId?: string;
    userType?: string;
    [key: string]: unknown;
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const initialGHLUserData: GHLUserData = {
  ssoToken: '',
  locationId: '',
  locationName: '',
  role: '',
  userName: '',
  userId: '',
  companyId: '',
  userType: '',
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  error: null,
  errorType: null,
  availableLocations: [],
  selectLocation: noop,
};

const GHL_SELECTED_LOCATION_KEY = 'ghl_selected_location';

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildOriginPatterns = (originList: string[]): RegExp[] => {
  return originList.map((origin) => {
    if (origin.startsWith('/') && origin.endsWith('/')) {
      return new RegExp(origin.slice(1, -1));
    }
    if (origin.includes('*')) {
      const pattern = escapeRegExp(origin).replace(/\\\*/g, '.*');
      return new RegExp(`^${pattern}$`);
    }
    if (origin.startsWith('http://') || origin.startsWith('https://')) {
      return new RegExp(`^${escapeRegExp(origin)}$`);
    }
    return new RegExp(`^https://${escapeRegExp(origin)}$`);
  });
};

// Allowed GHL parent origins (including white-label subdomains)
const ALLOWED_GHL_ORIGINS = [
  /^https:\/\/([a-z0-9-]+\.)?gohighlevel\.com$/,
  /^https:\/\/([a-z0-9-]+\.)?leadconnectorhq\.com$/,
  ...buildOriginPatterns(
    (import.meta.env.VITE_GHL_ALLOWED_ORIGINS || '')
      .split(',')
      .map((origin: string) => origin.trim())
      .filter(Boolean)
  ),
];

// Check if origin is a valid GHL domain
const isValidGHLOrigin = (origin: string): boolean => {
  return ALLOWED_GHL_ORIGINS.some((pattern) => pattern.test(origin));
};

// Report a terminal GHL SSO/handshake failure to Sentry. These failures happen
// entirely in the browser and never reach the backend, so without this they are
// invisible to us (WEBAPP-BUG-010). Synthesizes an Error for non-exception
// conditions so events group by the `ghl_sso_failure` reason tag. No-op when
// Sentry isn't initialized (e.g. local dev).
const captureSsoFailure = (
  reason: string,
  extra: Record<string, unknown> = {},
  cause?: unknown
): void => {
  const error = cause instanceof Error ? cause : new Error(`[GHLAPP] SSO failure: ${reason}`);
  Sentry.captureException(error, {
    tags: { app: 'ghl-embed', ghl_sso_failure: reason },
    extra,
  });
};

// Check if running in development mode (Vite's DEV flag)
const isDev = (): boolean => import.meta.env.DEV === true;

// Check if truly running on localhost (not staging/production)
const isTrueLocalhost = (): boolean => {
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Get the location hint: prefer URL query param (set by GHL via {{location.id}}),
// then fall back to localStorage (user's previous manual selection).
const getLocationHint = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlLocationId = params.get('location_id');
    if (urlLocationId && !urlLocationId.includes('{{')) {
      // Sync URL param to localStorage so the interceptor picks it up too
      localStorage.setItem(GHL_SELECTED_LOCATION_KEY, urlLocationId);
      return urlLocationId;
    }
    return localStorage.getItem(GHL_SELECTED_LOCATION_KEY) || '';
  } catch {
    return '';
  }
};

// GHL Context
export const GHLContext = createContext<GHLUserData>(initialGHLUserData);

/**
 * Custom hook for GHL Provider - handles postMessage communication with GHL parent iframe
 *
 * SECURITY MODEL:
 * 1. Origin Validation: All incoming messages are validated against ALLOWED_GHL_ORIGINS
 * 2. Source Validation: Messages must come from window.parent (the GHL iframe container)
 * 3. Token Requirement: SSO token is REQUIRED for authentication - locationId alone is insufficient
 * 4. Dev Bypass: Only allowed on true localhost (127.0.0.1/localhost) AND when DEV mode is enabled
 *
 * Note on postMessage '*': We use '*' for the initial REQUEST_USER_DATA because we don't know
 * the parent's origin until we receive a response. Security is enforced on the RECEIVING side
 * by validating event.origin against our allowlist. Once we receive a valid response, we store
 * the validated origin for any future communications.
 */
export const useGHLProvider = () => {
  const [ghlUser, setGHLUser] = useState<GHLUserData>(initialGHLUserData);
  // Store validated parent origin for future postMessage calls
  const validatedParentOrigin = useRef<string | null>(null);
  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const appId = import.meta.env.VITE_GHL_APP_ID;

    // SECURITY: Dev bypass requires BOTH true localhost AND dev mode
    // This prevents accidentally enabling bypass in staging/production
    const localSSOToken = import.meta.env.VITE_GHL_LOCAL_SSO_TOKEN;
    const canBypassSSO = isTrueLocalhost() && isDev() && localSSOToken;

    if (canBypassSSO) {
      // eslint-disable-next-line no-console
      console.log('[GHLAPP] Dev mode on localhost - using local SSO token');
      setGHLUser((prev) => ({
        ...prev,
        ssoToken: localSSOToken,
        locationId: import.meta.env.VITE_GHL_LOCAL_LOCATION_ID || '',
        userName: 'Dev User',
        role: 'admin',
        isAdmin: true,
        isLoading: false,
        isAuthenticated: true,
      }));
      return undefined;
    }

    // Production mode - request user data from GHL parent iframe
    const handleMessage = (event: MessageEvent) => {
      // SECURITY: Validate message source is the parent window
      if (event.source !== window.parent) {
        return;
      }

      // SECURITY: Validate origin is from allowed GHL domains
      if (!isValidGHLOrigin(event.origin)) {
        // eslint-disable-next-line no-console
        console.warn('[GHLAPP] Rejected message from untrusted origin:', event.origin);
        // Breadcrumb only — browser extensions post unrelated messages constantly,
        // so capturing each rejected origin as an event would flood Sentry. This
        // still attaches to any subsequent captured event (e.g. the timeout).
        Sentry.addBreadcrumb({
          category: 'ghl-sso',
          level: 'warning',
          message: 'Rejected message from untrusted origin',
          data: { origin: event.origin },
        });
        return;
      }

      // Store validated origin for future use
      validatedParentOrigin.current = event.origin;

      // Handle GHL response
      if (event.data?.message === 'REQUEST_USER_DATA_RESPONSE') {
        // eslint-disable-next-line no-console
        console.log('[GHLAPP] Received response from parent iframe', { origin: event.origin });
        const payload = event.data.payload || event.data;

        // Extract SSO token - GHL sends it as a string (encrypted token)
        const ssoToken = typeof payload === 'string' ? payload : (payload.ssoToken || '');

        if (!ssoToken) {
          // eslint-disable-next-line no-console
          console.warn('[GHLAPP] SSO token missing in response from parent iframe');
          captureSsoFailure('token_missing', { origin: event.origin });
          setGHLUser((prev) => ({
            ...prev,
            isLoading: false,
            isAuthenticated: false,
            error: 'SSO token is required for authentication',
            errorType: 'GENERIC',
          }));
          return;
        }

        // Call backend to decode SSO token and get user data
        fetchUserDataFromBackend(ssoToken);
      }
    };

    // AbortController for cancelling API requests on unmount
    const abortController = new AbortController();

    // Fetch user data from mongoose backend (decodes SSO token)
    const fetchUserDataFromBackend = async (ssoToken: string) => {
      try {
        // For custom menu links, GHL omits locationId from the SSO token.
        // Use the user's previously selected location from localStorage.
        const locationHint = getLocationHint();
        const response = await Api.get('/ghl/embed/me', {
          params: locationHint ? { locationHint } : undefined,
          headers: {
            'X-GHL-SSO': ssoToken,
          },
          signal: abortController.signal,
        });

        // Don't update state if component unmounted
        if (!isMounted.current) return;

        const { location_id, location_name, company_id, available_locations, payload: decodedPayload } = response.data as GHLMeResponse;

        const userData: Partial<GHLUserData> = {
          ssoToken,
          locationId: location_id || '',
          locationName: location_name || decodedPayload?.locationName || '',
          role: decodedPayload?.role || '',
          userName: decodedPayload?.name || decodedPayload?.userName || '',
          userId: decodedPayload?.userId || '',
          companyId: String(company_id || decodedPayload?.companyId || ''),
          userType: decodedPayload?.userType || '',
        };

        const isAdmin = userData.role === 'admin';

        setGHLUser((prev) => ({
          ...prev,
          ...userData,
          isAdmin,
          isLoading: false,
          isAuthenticated: true,
          error: null,
          availableLocations: available_locations || [],
        }));

        // eslint-disable-next-line no-console
        console.log('[GHLAPP] Authenticated successfully', {
          locationId: location_id,
          userName: userData.userName,
          role: userData.role,
        });
      } catch (fetchError) {
        // Ignore abort errors (component unmounted)
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return;
        }

        // Don't update state if component unmounted
        if (!isMounted.current) return;

        // eslint-disable-next-line no-console
        console.error('[GHLAPP] Failed to fetch user data from backend:', fetchError);

        // Parse error response to determine error type
        let errorMessage = 'Failed to verify SSO token with server';
        let errorType: GHLErrorType = 'GENERIC';
        let errorLocationId = '';

        // Check if this is an axios-like error with response data
        const axiosError = fetchError as { response?: { status?: number; data?: { error?: string; error_type?: string; available_locations?: GHLAvailableLocation[] } } };
        if (axiosError?.response?.data?.error) {
          const apiError = axiosError.response.data.error;
          errorMessage = apiError;

          // Check for location selection required (multiple locations available)
          if (axiosError.response.status === 403 && axiosError.response.data.error_type === 'LOCATION_SELECTION_REQUIRED') {
            errorType = 'LOCATION_SELECTION_REQUIRED';
            const locations = axiosError.response.data.available_locations || [];
            // Clear any stale stored hint that didn't resolve
            try {
              localStorage.removeItem(GHL_SELECTED_LOCATION_KEY);
            } catch { /* ignore */ }
            // eslint-disable-next-line no-console
            console.log('[GHLAPP] Location selection required, available:', locations.length);
            setGHLUser((prev) => ({
              ...prev,
              ssoToken,
              isLoading: false,
              isAuthenticated: false,
              error: errorMessage,
              errorType,
              availableLocations: locations,
            }));
            return;
          }

          // Check for "location not linked to company" error (403)
          if (axiosError.response.status === 403 && apiError.includes('not linked to a company')) {
            errorType = 'LOCATION_NOT_LINKED';
            // Extract location ID from error message: "GHL location 'XXX' is not linked..."
            const match = apiError.match(/GHL location '([^']+)'/);
            if (match) {
              errorLocationId = match[1];
            }
            // Clear stale localStorage hint so user isn't stuck in a loop
            try {
              localStorage.removeItem(GHL_SELECTED_LOCATION_KEY);
            } catch { /* ignore */ }
            // eslint-disable-next-line no-console
            console.warn('[GHLAPP] Location not linked to company:', errorLocationId);
          } else if (axiosError.response.status === 401) {
            errorType = 'INVALID_TOKEN';
          } else if (axiosError.response.status === 403) {
            // Clear stale hint on any other 403
            try {
              localStorage.removeItem(GHL_SELECTED_LOCATION_KEY);
            } catch { /* ignore */ }
          }
        }

        captureSsoFailure(errorType || 'GENERIC', {
          status: axiosError?.response?.status,
          locationHint: getLocationHint() || undefined,
          message: errorMessage,
        }, fetchError);

        setGHLUser((prev) => ({
          ...prev,
          ssoToken,
          locationId: errorLocationId || prev.locationId,
          isLoading: false,
          isAuthenticated: false,
          error: errorMessage,
          errorType,
        }));
      }
    };

    window.addEventListener('message', handleMessage);

    // Request user data from GHL parent
    // Note: Initial request uses '*' because we don't know parent origin yet
    // Security is enforced when RECEIVING the response via origin validation
    if (appId) {
      // eslint-disable-next-line no-console
      console.log('[GHLAPP] Requesting user data from parent iframe', { appId });
      window.parent.postMessage({ message: 'REQUEST_USER_DATA', appId }, '*');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[GHLAPP] No VITE_GHL_APP_ID configured');
      captureSsoFailure('app_id_missing');
      setGHLUser((prev) => ({
        ...prev,
        isLoading: false,
        error: 'GHL App ID not configured',
        errorType: 'GENERIC',
      }));
    }

    // Timeout for GHL response
    const timeout = setTimeout(() => {
      if (!isMounted.current) return;
      setGHLUser((prev) => {
        if (prev.isLoading) {
          // eslint-disable-next-line no-console
          console.warn('[GHLAPP] Timeout waiting for response from parent iframe (10s)');
          captureSsoFailure('timeout', { appId: appId || undefined, waitedMs: 10000 });
          return {
            ...prev,
            isLoading: false,
            error: 'Timeout waiting for GHL authentication',
            errorType: 'TIMEOUT',
          };
        }
        return prev;
      });
    }, 10000); // 10 second timeout

    return () => {
      isMounted.current = false;
      abortController.abort();
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, []);

  const selectLocation = useCallback((locationId: string) => {
    try {
      localStorage.setItem(GHL_SELECTED_LOCATION_KEY, locationId);
    } catch {
      // ignore storage errors
    }
    // Reload the page — the next mount will pick up the saved location from localStorage
    window.location.reload();
  }, []);

  const result: GHLUserData = {
    ...ghlUser,
    selectLocation,
  };

  return Object.freeze(result);
};

// Legacy export for backwards compatibility (deprecated - use useGHLProvider)
export const ghlProviderFunctions = useGHLProvider;

// Hook to use GHL context
export const useGHL = () => useContext(GHLContext);
